import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    AppBar,
    Toolbar,
    Grid,
    Typography,
    IconButton,
    Modal,
    TextField
} from '@mui/material';
import {Add, Folder, Delete, CloudUpload, ArrowBack, DocumentScanner} from '@mui/icons-material';
import { Pagination } from '@mui/material';
import HeaderAvatar from "../components/HeaderAvatar";
import { usePermissions } from "../components/PermissionsContext";
import {
    createPDFFolder,
    getPDFFolders,
    getPDFs,
    uploadPDF,
    deletePDFFolder,
    deletePDF
} from '../services/api';
import { DocumentScanner as DocumentScannerIcon } from '@mui/icons-material';
import { LinearProgress } from '@mui/material';
import Icon from "../components/Icon";

interface Folder {
    id: string;
    name: string;
}

interface PDFFile {
    id: string;
    name: string;
}
interface FolderItemProps {
    folder: Folder;
    onOpen: (folder: Folder) => void;
    onDelete: (item: Folder, type: 'folder') => void;
}
interface PDF {
    id: string;
    name: string;
    file_size: number;
    url: string;
}
interface PDFItemProps {
    pdf: PDF;
    onDelete: (item: PDF, type: 'pdf') => void;
}
interface UploadAreaProps {
    onUpload: (file: File) => void;
}
interface UploadModalProps {
    open: boolean;
    onClose: () => void;
    progress: number;
}
interface DeleteConfirmationModalProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    item: { type: 'folder' | 'pdf' } | null;
}

const Instructions: React.FC = () => {
    const { permissions } = usePermissions();
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState<string>('created_at');
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isFolderCreateModalOpen, setIsFolderCreateModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<(Folder | PDFFile) & { type: 'folder' | 'pdf' } | null>(null);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [pdfs, setPDFs] = useState<PDF[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    // const [currentFolder, setCurrentFolder] = useState<string | null>(null);
    const [currentFolder, setCurrentFolder] = useState<Folder | null>(null);
    const [createFolderName, setCreateFolderName] = useState('');
    const [showDelete, setShowDelete] = useState(false);

    useEffect(() => {
        setPDFs([]);
        fetchFoldersAndPDFs(currentPage, currentFolder?.id);
    }, [currentPage, currentFolder]);



    const fetchFoldersAndPDFs = async (page: number, folderId: string | null = null) => {
        try {
            const foldersResponse = await getPDFFolders();
            setFolders(foldersResponse.items);

            const pdfsResponse = await getPDFs({
                page,
                limit: 24,
                folder_id: folderId,
                sortBy: sortBy // Add this if you're using sorting
            });
            setPDFs(pdfsResponse.items);
            setTotalPages(pdfsResponse.total_pages);
            setCurrentPage(page);
        } catch (error) {
            console.error('Error fetching folders and pdfs:', error);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Байт';

        const k = 1024;
        const sizes = ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleCreateFolder = async () => {

        try {
            await createPDFFolder(createFolderName);
            fetchFoldersAndPDFs(currentPage, currentFolder?.id);
            setIsFolderCreateModalOpen(false)
        } catch (error) {
            console.error('Error creating folder:', error);
        }

    };

    const handleOpenFolder = (folder: Folder) => {
        setCurrentFolder(folder);
    };

    const handleUpload = async (file: File) => {
        setIsUploadModalOpen(true);
        try {
            const response = await uploadPDF(file, currentFolder?.id || null, (progress) => {
                setUploadProgress(progress);
            });
            fetchFoldersAndPDFs(currentPage, currentFolder?.id || null);
        } catch (error) {
            console.error('Error uploading pdf:', error);
        } finally {
            setIsUploadModalOpen(false);
            setUploadProgress(0);
        }
    };

    const handleDeletePrompt = (item: Folder | PDFFile, type: 'folder' | 'pdf') => {
        setItemToDelete({ ...item, type });
        setIsDeleteModalOpen(true);
    };

    const handleDelete = async () => {
        if (itemToDelete) {
            try {
                if (itemToDelete.type === 'folder') {
                    await deletePDFFolder(itemToDelete.id);
                } else {
                    await deletePDF(itemToDelete.id);
                }
                fetchFoldersAndPDFs(currentPage, currentFolder?.id);
            } catch (error) {
                console.error('Error deleting item:', error);
            } finally {
                setIsDeleteModalOpen(false);
                setItemToDelete(null);
            }
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && file.type === 'application/pdf') {
            handleUpload(file);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleUpload(file);
        }
    };

    const PDFItem: React.FC<{ pdf: PDF }> = ({ pdf }) => {
        const openPDFInNewTab = () => {
            window.open(pdf.url, '_blank', 'noopener,noreferrer');
        };
        return (
            <Box className='fileItem' sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Box sx={{display: 'flex', cursor: 'pointer'}} onClick={openPDFInNewTab}>
                    <DocumentScannerIcon sx={{mr: 1}}/>
                    <Typography>{pdf.name}</Typography>
                </Box>
                <Box className='fileItemRight' sx={{display: 'flex'}}>
                    <Typography sx={{mr: 2}}>{formatFileSize(pdf.file_size)}</Typography>
                    <Box
                        sx={{cursor: 'pointer'}}
                        onClick={() => handleDeletePrompt(pdf, 'pdf')}
                    >
                        <Icon name="remove"/>
                    </Box>
                </Box>
            </Box>
        )
    };

    const UploadArea: React.FC<UploadAreaProps> = ({ onUpload }) => (
        <Box
            sx={{
                border: '2px dashed grey',
                borderRadius: 2,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer'
            }}
            onClick={() => document.getElementById('pdf-upload')?.click()}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
        >
            <CloudUpload sx={{ fontSize: 40 }} />
            <Typography>Нажмите или перетащите файл PDF</Typography>
            <input
                id="pdf-upload"
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
        </Box>
    );


    const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({ open, onClose, onConfirm, item }) => (
        <Box sx={{ /* стили */ }}>
            <Typography>
                {item?.type === 'folder'
                    ? 'Вы уверены, что хотите удалить эту папку? Все файлы внутри папки также будут удалены.'
                    : 'Вы уверены, что хотите удалить этот документ?'}
            </Typography>
            <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2, gap: 2}}>
                <Button className="blueModalButton" variant="contained" onClick={onConfirm}>
                    Удалить
                </Button>
                <Button className="blueModalButton" variant="outlined" onClick={onClose} style={{color: 'red', borderColor: 'red'}}>
                    Отмена
                </Button>
            </Box>
        </Box>
    );



    return (
        <Box>
            <AppBar position="static">
                <Toolbar className='toolbar'>
                    <Box className='toolbarTitle'>Инструкция</Box>
                    <Button className="addToobarBtn" variant="contained" onClick={() => setIsFolderCreateModalOpen(true)}><Add /></Button>
                    <HeaderAvatar/>
                </Toolbar>
            </AppBar>

            {/* Основной контент */}
            {!currentFolder ?
                <>
                    <Box className='topButtonsContainer hideMPageButtons'>
                        <Typography variant="h6">Папки</Typography>
                        <Button className='topButtonsAdd' variant="outlined" onClick={() => setIsFolderCreateModalOpen(true)}><Add /> Добавить папку</Button>
                    </Box>
                    {folders.length == 0 ?
                        <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 3, mb: 3}}>
                            <Typography>Пока нет ни одной папки</Typography>
                        </Box>
                        :
                        <Box sx={{p: 3}}>
                            <Box sx={{mb: 4, display: 'flex', flexWrap: 'wrap'}}>
                                {folders.map((folder) => (
                                    <Box className='folderItem' onClick={() => setCurrentFolder(folder)}>
                                        <Folder sx={{fontSize: 60}}/>
                                        <Typography>{folder.name}</Typography>
                                        <IconButton
                                            className='folderItemRemove'
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePrompt(folder, 'folder');
                                            }}
                                        >
                                            <Delete/>
                                        </IconButton>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    }
                    <Box className='topButtonsContainer hideMPageButtons'>
                        <Typography variant="h6">Файлы</Typography>
                    </Box>
                </>
                :
                <Box className='topButtonsContainer hideMPageButtons folderBack' sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center'}}>
                    <Button sx={{color: '#3a485b', padding: 0}} onClick={() => setCurrentFolder(null)}>
                        <ArrowBack sx={{color: '#3a485b'}} />
                    </Button>
                    <Typography variant="h6">{currentFolder.name}</Typography>
                </Box>
            }
            <Box>
                <Box sx={{ p: 3 }}>
                    <Grid item xs={3} sx={{mb:4}}>
                        <UploadArea onUpload={handleUpload} />
                    </Grid>
                    {pdfs.map((pdf) => (
                        <PDFItem pdf={pdf} />
                    ))}

                    {totalPages > 1 ?
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(_, page) => setCurrentPage(page)}
                            sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
                        />
                        : null }
                </Box>
            </Box>

            {/* Модальные окна */}
            <Modal open={isUploadModalOpen} onClose={() => setIsUploadModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                        <Box sx={{ /* стили */ }}>
                            <Typography sx={{mb: 3}}>Пожалуйста, дождитесь завершения загрузки</Typography>
                            <LinearProgress variant="determinate" value={uploadProgress} />
                            <Typography>{uploadProgress}%</Typography>
                        </Box>
                    </Box>
                </Box>
            </Modal>

            <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 360 }}>
                        <DeleteConfirmationModal
                            open={isDeleteModalOpen}
                            onClose={() => setIsDeleteModalOpen(false)}
                            onConfirm={handleDelete}
                            item={itemToDelete}
                        />
                    </Box>
                </Box>
            </Modal>




            {/* Модальное окно создания папки */}
            <Modal open={isFolderCreateModalOpen} onClose={() => setIsFolderCreateModalOpen(false)}>
                <Box sx={{flexGrow:1, minHeight: '100vh', display: 'flex', justifyContent:'center', alignItems:'center'}}>
                    <Box className="modalFiled" sx={{ position: 'relative', bgcolor: 'background.paper', boxShadow: 24, maxWidth: 600 }}>
                        <Typography variant="h6">Добавить папку</Typography>
                        <TextField
                            label="Название"
                            value={createFolderName}
                            onChange={(e) => setCreateFolderName(e.target.value)}
                            fullWidth
                            type="text"
                            margin="normal"
                        />
                        <Box sx={{display:'flex', justifyContent: 'space-between', mt: 2, gap: 2}}>
                            <Button className="blueModalButton" variant="contained" onClick={handleCreateFolder}>
                                Добавить
                            </Button>
                            <Button className="blueModalButton" variant="outlined" onClick={()=>setIsFolderCreateModalOpen(false)} style={{color: 'red', borderColor: 'red'}}>
                                Отмена
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};

export default Instructions;